import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { format, parse, isValid } from 'date-fns';

import Icon from '../../../../../shared/_TBComponents/Icon';
import MaxesGoalsTable, {
  OverflowCell, ClampedCell, Dash, Pill,
} from './MaxesGoalsTable';
import { ActionsWrapper, ActionButton } from './MaxesTable';

const ACHIEVEMENT_ICON = 'trophy';
const IMPROVEMENT_COLOR = '#1E8449';

const ChangeText = styled('span', {
  shouldForwardProp: (prop) => prop !== 'isImprovement',
})`
  overflow: hidden;
  font-family: "Nunito Sans";
  font-size: 14px;
  font-weight: 700;
  color: ${(props) => (props.isImprovement ? IMPROVEMENT_COLOR : props.theme.colors.textError)};
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ResultWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const TableSection = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
`;

const DateCell = ({ value }) => {
  const parsed = value ? parse(value, 'yyyy-MM-dd', new Date()) : null;
  return <OverflowCell value={parsed && isValid(parsed) ? format(parsed, 'd MMMM yyyy') : value} />;
};

DateCell.propTypes = { value: PropTypes.string };
DateCell.defaultProps = { value: '' };

const ResultCell = ({ row }) => {
  const { displayValue, isCurrentMax } = row.original;
  return (
    <ResultWrapper>
      <OverflowCell value={displayValue} />
      {isCurrentMax && <Pill>Current</Pill>}
    </ResultWrapper>
  );
};

ResultCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      displayValue: PropTypes.string,
      isCurrentMax: PropTypes.bool,
    }),
  }).isRequired,
};

const ChangeCell = ({ row }) => {
  const { displayChange, isImprovement } = row.original;
  if (!displayChange) return <Dash />;
  return <ChangeText isImprovement={isImprovement}>{displayChange}</ChangeText>;
};

ChangeCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      displayChange: PropTypes.string,
      isImprovement: PropTypes.bool,
    }),
  }).isRequired,
};

// The goal value is only known for the current goal
const AchievementCell = ({ row }) => {
  const { achievementValue, achievementScope } = row.original;
  if (!achievementScope) return <Dash />;
  return (
    <ResultWrapper>
      <Icon icon={ACHIEVEMENT_ICON} size={14} color='#E8A33A' />
      <OverflowCell value={achievementValue || 'Goal achieved'} />
    </ResultWrapper>
  );
};

AchievementCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      achievementValue: PropTypes.string,
      achievementScope: PropTypes.string,
    }),
  }).isRequired,
};

const RowActions = styled(ActionsWrapper)`
  width: 100%;
`;

const RowActionButton = styled(ActionButton, {
  shouldForwardProp: (prop) => prop !== 'isHidden',
})`
  flex-shrink: 0;
  white-space: nowrap;
  visibility: ${(props) => (props.isHidden ? 'hidden' : 'visible')};

  &:disabled {
    opacity: 0.4;
    cursor: default;
    background: #FFFFFF;
  }
`;

const ActionsCell = ({ row }) => {
  const { isCurrentMax, onSetActive, onArchive } = row.original;
  const handleSetActive = () => onSetActive(row.original);
  const handleDelete = () => onArchive(row.original);
  return (
    <RowActions>
      <RowActionButton
        type='button'
        isHidden={isCurrentMax}
        aria-hidden={isCurrentMax}
        onClick={handleSetActive}
      >
        <Icon icon='ribbon' size={12} />
        Make current
      </RowActionButton>
      <RowActionButton type='button' onClick={handleDelete}>
        <Icon icon='delete' size={12} color='#FF0505' />
        Delete
      </RowActionButton>
    </RowActions>
  );
};

ActionsCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      isCurrentMax: PropTypes.bool,
      onSetActive: PropTypes.func,
      onArchive: PropTypes.func,
    }),
  }).isRequired,
};

const SourceWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const WorkoutLink = styled.a`
  display: flex;
  padding: 4px;
  border-radius: 4px;
  line-height: 0;
  color: ${(props) => props.theme.colors.text};

  &:hover {
    color: ${(props) => props.theme.colors.hover};
    background: rgba(119, 167, 255, 0.15);
  }
`;

const SourceCell = ({ row }) => {
  const { sourceLabel: label, workoutHref } = row.original;
  if (!label) return <Dash />;
  return (
    <SourceWrapper>
      <ClampedCell value={label} />
      {workoutHref && (
        <WorkoutLink
          href={workoutHref}
          target='_blank'
          rel='noopener noreferrer'
          title='View workout'
          aria-label='View workout'
        >
          <Icon icon='calendar' size={14} color='currentColor' />
        </WorkoutLink>
      )}
    </SourceWrapper>
  );
};

SourceCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      sourceLabel: PropTypes.string,
      workoutHref: PropTypes.string,
    }),
  }).isRequired,
};

// Sorting is off so maxes stay newest first
const COLUMNS = [
  {
    Header: 'Date',
    accessor: 'dateSet',
    disableSortBy: true,
    headerStyle: { width: '200px' },
    Cell: DateCell,
  },
  {
    Header: 'Result',
    id: 'result',
    disableSortBy: true,
    headerStyle: { minWidth: '180px', flex: '1' },
    cellStyle: { minWidth: '180px', flex: '1' },
    Cell: ResultCell,
  },
  {
    Header: 'Change',
    id: 'change',
    disableSortBy: true,
    headerStyle: { width: '160px' },
    Cell: ChangeCell,
  },
  {
    Header: 'Goal Achieved',
    id: 'achievement',
    disableSortBy: true,
    headerStyle: { minWidth: '180px', flex: '1' },
    cellStyle: { minWidth: '180px', flex: '1' },
    Cell: AchievementCell,
  },
  {
    Header: 'Source',
    id: 'source',
    disableSortBy: true,
    headerStyle: { minWidth: '180px', flex: '1' },
    cellStyle: { minWidth: '180px', flex: '1' },
    Cell: SourceCell,
  },
  {
    Header: 'Actions',
    id: 'actions',
    sticky: 'right',
    disableSortBy: true,
    headerStyle: { width: '260px', textAlign: 'center' },
    cellStyle: { width: '260px', textAlign: 'center' },
    Cell: ActionsCell,
  },
];

const MaxHistoryTable = ({ rows, isLoading }) => {
  const columns = useMemo(() => COLUMNS, []);

  return (
    <TableSection>
      <MaxesGoalsTable
        flush
        data={rows}
        columns={columns}
        isLoading={isLoading}
        hideSearch
        emptyIcon='lift'
        entityLabel='max'
        defaultSortBy={[]}
        emptyMessage='No maxes recorded yet.'
      />
    </TableSection>
  );
};

MaxHistoryTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    dateSet: PropTypes.string,
    displayValue: PropTypes.string,
    displayChange: PropTypes.string,
    isImprovement: PropTypes.bool,
    achievementValue: PropTypes.string,
    achievementScope: PropTypes.string,
    isCurrentMax: PropTypes.bool,
    workoutHref: PropTypes.string,
    onSetActive: PropTypes.func,
    onArchive: PropTypes.func,
    sourceLabel: PropTypes.string,
  })),
  isLoading: PropTypes.bool,
};

MaxHistoryTable.defaultProps = {
  rows: [],
  isLoading: false,
};

export default MaxHistoryTable;
